import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { PublicationFeaturedDto } from '@app/data/dtos/multimedia/publication/publication-featured.dto';
import { mapPublicationFeatured } from '@app/data/mappers/multimedia/publication/publication-featured.mapper';
import { PublicationRepository } from '@app/domain/repositories/multimedia/publication/publication.repository.contract';
import { IPublicationRequestParams } from '@app/entities/interfaces/multimedia/publication.request.interface';
import { AppError, ErrorCodes, ErrorTypes } from '@app/entities/models/error/error.model';
import { MultimediaMetadata } from '@app/entities/models/multimedia/multimedia-metadata.model';
import {
  AbstractPaginatedMultimediaItemResponse,
  AbstractMultimediaItem
} from '@app/entities/models/multimedia/multimedia.abstract.model';
import { MultimediaFactory } from '@app/entities/models/multimedia/multimedia.factory';
import { Pagination } from '@app/entities/models/multimedia/pagination.model';
import { PublicationFeatured } from '@app/entities/models/multimedia/publication-featured.model';
import { Publication } from '@app/entities/models/multimedia/publication.model';
import { Logger } from '@app/services';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { ErrorService } from '@services/error/error.service';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';
import { LanguageService } from '@services/language/language.service';

import { MRepository } from '@mercadona/core/utils/repository';

import { HttpOptions } from '@/models/http.options.model';

@Injectable({
  providedIn: 'root'
})
export class PublicationRepositoryImpl extends MRepository implements PublicationRepository {
  private logger = new Logger('PublicationService');
  url = '/publication';
  urlPublicationFeatured: string = '/api/publications/highlighted';

  constructor(
    private errorService: ErrorService,
    private languageService: LanguageService
  ) {
    super(`${ENV.API_URL}`);
  }

  getPaginatedItems(params: IPublicationRequestParams = {}): Observable<AbstractPaginatedMultimediaItemResponse> {
    return this.languageService.getCurrentLanguage().pipe(
      mergeMap((lang) => {
        const options: MSafeAny = {
          observe: 'response',
          params: { lang, ...params },
          context: new HttpContext().set(SECTION_KEY, 'publications')
        };
        return this.get(this.url, options);
      }),
      map((response: MSafeAny) => {
        const pagination: Pagination = new Pagination(response.headers);
        const items: Publication[] = response.body.map((item: AbstractMultimediaItem) =>
          MultimediaFactory.createPublication(item)
        );
        return new AbstractPaginatedMultimediaItemResponse(items, pagination, 0);
      })
    );
  }

  getItemWithMetadata(translationKey: string, params: object = {}): Observable<MultimediaMetadata<Publication>> {
    return this.languageService.getCurrentLanguage().pipe(
      mergeMap((lang) =>
        this.get<Publication>(`${this.url}/${translationKey}`, {
          observe: 'response',
          params: { lang, ...params },
          context: new HttpContext().set(SECTION_KEY, 'publication')
        }).pipe(
          catchError((err) => {
            if (err.status === ErrorCodes.NOT_FOUND) {
              this.errorService.add(new AppError(ErrorTypes.NOT_FOUND, err));
            } else {
              this.errorService.add(new AppError(ErrorTypes.OTHER, err));
            }
            return throwError(() => err);
          })
        )
      ),
      map((response: MSafeAny) => {
        const item = MultimediaFactory.createPublication(response.body);
        return new MultimediaMetadata(item, response.headers);
      })
    );
  }

  getItem(translationKey: string, params: object = {}): Observable<Publication> {
    return this.getItemWithMetadata(translationKey, params).pipe(map((i) => i.item));
  }

  getPublicItem(slug: string, params: object = {}): Observable<Publication> {
    return this.languageService.getCurrentLanguage().pipe(
      mergeMap((lang) => this.get<Publication>(`${this.url}/public/${slug}`, new HttpOptions({ lang, ...params }))),
      map((item: Publication) => MultimediaFactory.createPublication(item))
    );
  }

  updateItemVisualizations(translationKey: string): Observable<number> {
    return this.post(`${this.url}/${translationKey}/views`, {}).pipe(
      map((result: MSafeAny) => parseInt(result.views, 10))
    );
  }

  getPublicationFeatured(): Observable<PublicationFeatured> {
    return this.get<PublicationFeaturedDto>(this.urlPublicationFeatured).pipe(map(mapPublicationFeatured));
  }
}
